import { Unit } from './Unit.js';
import { SoldierAI } from '../ai/SoldierAI.js';
import { RadioSystem } from '../systems/unit/RadioSystem.js';
import {
  GAME_CONFIG, AI_CONFIG, RENDER_CONFIG, BERSERKER_CONFIG, BASE_CONFIG,
  RESOURCE_CONFIG, MOVEMENT_CONFIG, COMBAT_CONFIG, ANIMATION_CONFIG,
  UI_CONFIG, THRESHOLD_CONFIG, DEBUG_VISUALIZATION_CONFIG
} from '../config.js';
import { getDistance, clamp, randomRange } from '../utils/math.js';
import { isDebugMode, debugLog, debugWarn, debugError } from '../utils/debug.js';
import { Projectile } from './Projectile.js';
import { Resource } from './Resource.js';
import { audioManager } from '../systems/AudioManager.js';

/**
 * Soldier class - represents a combat unit in the game
 *
 * Soldiers are AI-driven units that:
 * - Fight enemy units and bases using ranged weapons
 * - Collect resources (metal, nebulas, crystals, corpses)
 * - Level up through combat, gaining stats and abilities
 * - Reach max level to become powerful berserkers
 * - Coordinate with teammates via radio system
 * - Use a finite state machine for complex behavioral AI
 *
 * State Machine: Managed by SoldierAI, transitions between states like:
 * idle, hunting, fighting, fleeing, gathering, returning_resource, etc.
 *
 * Resource Economy: Soldiers collect metal/organics for their base,
 * spend ammo in combat, and must return to base to resupply.
 */
export class Soldier extends Unit {
  /**
   * Create a new soldier unit
   * @param {number} x - Initial x coordinate
   * @param {number} y - Initial y coordinate
   * @param {HTMLCanvasElement} canvas - Game canvas for boundary checking
   * @param {Object} team - Team configuration object with id, name, color
   * @param {TeamBase|null} base - Home base for this soldier (null for base-less)
   */
  constructor(x, y, canvas, team, base = null) {
    super(x, y, canvas, team, base);
    
    // Soldier specific properties
    this.level = 1;
    this.kills = 0;
    this.skill = randomRange(AI_CONFIG.SKILL_RANGE.min, AI_CONFIG.SKILL_RANGE.max);
    
    // Combat properties
    this.weaponRange = GAME_CONFIG.BASE_WEAPON_RANGE;
    this.perceptionRange = AI_CONFIG.PERCEPTION_RANGE;
    this.damage = GAME_CONFIG.BASE_DAMAGE;
    this.maxAmmo = GAME_CONFIG.AMMO_CAPACITY;
    this.ammo = this.maxAmmo;
    this.muzzleFlash = 0;
    
    // Courage system
    this.courage = AI_CONFIG.MAX_COURAGE;
    this.maxCourage = AI_CONFIG.MAX_COURAGE;
    this.lastDamageTime = 0;
    this.damageIndicator = 0; // Hit effect timer
    
    // Resource carrying
    this.carryingResource = null; // For backwards compatibility
    this.metalCargo = []; // Array of metal resources being carried
    
    // Berserker mode
    this.isBerserker = false;
    this.berserkerGlow = 0;
    
    // Knockback effects
    this.knockbackTime = 0;
    this.fleeFromBase = null;
    
    // Register unit systems
    const radioSystem = this.registerSystem('radio', new RadioSystem());

    // Create AI
    this.ai = new SoldierAI(this);

    if (radioSystem && typeof radioSystem.setAI === 'function') {
      radioSystem.setAI(this.ai);
    }
    
    // Override base speed with variation
    this.speed = GAME_CONFIG.BASE_SPEED * randomRange(MOVEMENT_CONFIG.SOLDIER_SPEED_MIN, MOVEMENT_CONFIG.SOLDIER_SPEED_MAX);
    
    // Spawn fade-in animation
    this.opacity = 0;
    this.fadeInSpeed = ANIMATION_CONFIG.SOLDIER_SPAWN_FADE_SPEED;
    
    // Set initial state to idle (state machine will handle transition)
    this.setState('idle');
    
    // Store reference to game containers
    this.projectiles = [];
    this.allBases = [];
    
    // Target tracking for debug rendering (compatible with original)
    this.target = null;
    this.lastPredictedX = 0;
    this.lastPredictedY = 0;
    
    // Facing direction for smooth turning
    this.facingAngle = Math.random() * Math.PI * 2; // Start facing random direction

    // Temporary override to keep eyes on a target while repositioning
    this.faceTargetOverride = null;
  }

  /**
   * Override updateMovement to add smooth turning and forward movement
   *
   * Soldiers face the direction they're moving/fighting with smooth rotation.
   * They can face a target override (for backpedaling/strafing) independent of movement.
   *
   * @param {number} delta - Time delta in milliseconds since last update
   */
  updateMovement(delta) {
    const hasVelocity = this.vx !== 0 || this.vy !== 0;
    let desiredAngle = null;
    let usingTargetOverride = false;
    let overrideMode = 'forward';

    const override = this.faceTargetOverride;
    let overrideTarget = null;

    if (override) {
      if (override.target) {
        overrideTarget = override.target;
        overrideMode = override.mode || override.movementMode || 'forward';
      } else {
        overrideTarget = override;
      }
    }

    if (overrideTarget && typeof overrideTarget.x === 'number' && typeof overrideTarget.y === 'number') {
      const dx = overrideTarget.x - this.x;
      const dy = overrideTarget.y - this.y;

      if (dx !== 0 || dy !== 0) {
        desiredAngle = Math.atan2(dy, dx);
        usingTargetOverride = true;
      } else {
        // Target is on top of us, clear override to avoid jitter
        this.clearFaceTargetOverride();
      }

      if (overrideTarget && overrideTarget.alive === false) {
        // Target died while we were backing away
        this.clearFaceTargetOverride();
        usingTargetOverride = false;
        desiredAngle = null;
      }
    }

    if (desiredAngle === null && hasVelocity) {
      desiredAngle = Math.atan2(this.vy, this.vx);
    }

    if (desiredAngle !== null) {
      const speed = hasVelocity ? Math.sqrt(this.vx * this.vx + this.vy * this.vy) : 0;

      let angleDiff = desiredAngle - this.facingAngle;

      while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
      while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

      if (Math.abs(angleDiff) > MOVEMENT_CONFIG.MAX_TURN_RATE) {
        angleDiff = Math.sign(angleDiff) * MOVEMENT_CONFIG.MAX_TURN_RATE;
      }

      this.facingAngle += angleDiff;

      while (this.facingAngle < 0) this.facingAngle += 2 * Math.PI;
      while (this.facingAngle >= 2 * Math.PI) this.facingAngle -= 2 * Math.PI;

      if (hasVelocity) {
        if (usingTargetOverride) {
          if (overrideMode === 'backpedal') {
            const backwardAngle = this.facingAngle + Math.PI;
            this.vx = Math.cos(backwardAngle) * speed;
            this.vy = Math.sin(backwardAngle) * speed;
          } else {
            this.vx = Math.cos(this.facingAngle) * speed;
            this.vy = Math.sin(this.facingAngle) * speed;
          }
        } else {
          this.vx = Math.cos(this.facingAngle) * speed;
          this.vy = Math.sin(this.facingAngle) * speed;
        }
      }
    }

    // Call parent movement update
    super.updateMovement(delta);
  }

  /**
   * Set a target to face while moving (for backpedaling or strafing)
   * Used when soldier needs to look at enemy while retreating.
   *
   * @param {Object} target - Entity to face (must have x, y coordinates)
   * @param {Object} options - Movement options
   * @param {string} options.mode - 'forward' or 'backpedal' movement mode
   */
  setFaceTargetOverride(target, options = {}) {
    if (!target || typeof target.x !== 'number' || typeof target.y !== 'number') {
      this.clearFaceTargetOverride();
      return;
    }

    const requestedMode = options.mode || options.movementMode;
    const movementMode = requestedMode === 'backpedal' ? 'backpedal' : 'forward';

    this.faceTargetOverride = {
      target,
      mode: movementMode
    };
  }

  /**
   * Clear the face target override
   * @param {Object|null} target - Optional: only clear if currently facing this specific target
   */
  clearFaceTargetOverride(target = null) {
    if (!this.faceTargetOverride) {
      return;
    }

    const currentTarget = this.faceTargetOverride.target || this.faceTargetOverride;

    if (target && currentTarget && currentTarget !== target) {
      return;
    }

    this.faceTargetOverride = null;
  }

  /**
   * Check if soldier has an active face target override
   * @returns {boolean} True if actively overriding face direction
   */
  hasFaceTargetOverride() {
    if (!this.faceTargetOverride) {
      return false;
    }

    const target = this.faceTargetOverride.target || this.faceTargetOverride;
    return !!(target && typeof target.x === 'number' && typeof target.y === 'number');
  }
  
  /**
   * Main update loop for soldier - called every frame
   *
   * Handles:
   * - Fade-in animation for new spawns
   * - Visual effects (muzzle flash, damage indicator, berserker glow)
   * - Courage recovery over time
   * - Knockback physics from base missiles
   * - Separation force to prevent teammate clustering
   * - Base avoidance (don't walk into enemy bases)
   * - AI decision making and state execution
   * - Healing/resupplying when at friendly base
   * - Ammo donation to base when needed
   *
   * @param {Soldier[]} soldiers - All soldiers in the game
   * @param {Projectile[]} projectiles - All projectiles in the game
   * @param {Resource[]} resources - All resources in the game
   * @param {TeamBase[]} bases - All bases in the game
   */
  update(soldiers, projectiles, resources, bases) {
    if (!this.alive) return;
    
    // Calculate delta time
    const now = Date.now();
    const delta = now - this.lastUpdate;
    this.lastUpdate = now;
    
    // Store references for AI and other methods
    this.projectiles = projectiles;
    this.allBases = bases;
    
    // Handle fade-in animation
    if (this.opacity < 1.0) {
      this.opacity += this.fadeInSpeed;
      if (this.opacity > 1.0) {
        this.opacity = 1.0;
      }
    }
    
    // Update visual effects
    this.updateEffects(delta);
    
    // Additional soldier-specific effects
    if (this.muzzleFlash > 0) {
      this.muzzleFlash -= ANIMATION_CONFIG.MUZZLE_FLASH_DECAY * (1 + (this.level - 1) * ANIMATION_CONFIG.MUZZLE_FLASH_LEVEL_BONUS);
    }
    
    if (this.damageIndicator > 0) {
      this.damageIndicator = Math.max(0, this.damageIndicator - COMBAT_CONFIG.DAMAGE_INDICATOR_DECAY);
    }
    
    if (this.berserkerGlow > 0) {
      this.berserkerGlow = BERSERKER_CONFIG.GLOW_MIN_INTENSITY + Math.sin(Date.now() * BERSERKER_CONFIG.GLOW_SPEED) * BERSERKER_CONFIG.GLOW_VARIATION;
    }
    
    // Update courage
    if (now - this.lastDamageTime > AI_CONFIG.HEAL_DELAY && this.courage < this.maxCourage) {
      this.courage = Math.min(this.maxCourage, this.courage + delta * THRESHOLD_CONFIG.COURAGE_RECOVERY_RATE);
    }
    
    // Handle knockback
    if (this.knockbackTime > 0) {
      this.knockbackTime--;
      this.vx *= MOVEMENT_CONFIG.KNOCKBACK_VELOCITY_DECAY;
      this.vy *= MOVEMENT_CONFIG.KNOCKBACK_VELOCITY_DECAY;
      
      if (Math.abs(this.vx) < MOVEMENT_CONFIG.MIN_VELOCITY_THRESHOLD && Math.abs(this.vy) < MOVEMENT_CONFIG.MIN_VELOCITY_THRESHOLD) {
        this.vx = 0;
        this.vy = 0;
        this.knockbackTime = 0;
      }
    } else {
      // Apply separation force before AI update
      this.applySeparationForce(soldiers.filter(s => s !== this && s.alive && s.team.id === this.team.id));
      
      // Apply base avoidance
      this.applyBaseAvoidanceForce(bases);
    }
    
    // Update unit systems (radio, etc.)
    this.updateSystems(delta, soldiers, projectiles, resources, bases);

    // Let AI handle decisions and movement
    this.ai.update(delta, soldiers, projectiles, resources, bases);
    
    // Handle base interactions (healing/reloading at base)
    if ((this.state === 'fleeing' || this.state === 'reloading' || this.state === 'seeking_base' || this.state === 'withdrawing') && 
        this.base && this.base.alive && this.base.isAtCenter && this.base.isAtCenter(this)) {
      
      const healPerMs = this.maxHealth / AI_CONFIG.HEAL_TIME;
      const ammoPerMs = this.maxAmmo / AI_CONFIG.RESUPPLY_TIME;
      
      if (this.health < this.maxHealth) {
        this.health = Math.min(this.maxHealth, this.health + healPerMs * delta);
      }
      if (this.courage < this.maxCourage) {
        this.courage = Math.min(this.maxCourage, this.courage + delta / THRESHOLD_CONFIG.COURAGE_RECOVERY_DIVISOR);
      }
      if (this.ammo < this.maxAmmo && this.base.giveAmmo) {
        this.base.giveAmmo(this);
      }
      
      // Donate ammo to base if it needs it and soldier is fully supplied
      if (this.state === 'reloading' && this.base.priority === 'get_ammo' && this.ammo >= this.maxAmmo * THRESHOLD_CONFIG.AMMO_DONATION_THRESHOLD) {
        const readyToLeave = this.health >= this.maxHealth * THRESHOLD_CONFIG.HEALTH_READY_THRESHOLD && this.courage >= this.maxCourage * THRESHOLD_CONFIG.COURAGE_READY_THRESHOLD && this.ammo >= this.maxAmmo * THRESHOLD_CONFIG.AMMO_READY_THRESHOLD;
        if (!readyToLeave) {
          // Level 5+ soldiers donate more ammo
          const donationMultiplier = this.level >= 5 ? COMBAT_CONFIG.HIGH_LEVEL_DONATION_MULTIPLIER : 1.0;
          const donationAmount = Math.min(this.ammo * COMBAT_CONFIG.AMMO_DONATION_PERCENTAGE * donationMultiplier, BASE_CONFIG.MISSILE_AMMO_COST * COMBAT_CONFIG.MISSILE_DONATION_AMOUNT * donationMultiplier);
          if (this.base.depositAmmo) {
            const actualDonation = this.base.depositAmmo(donationAmount);
            this.ammo -= actualDonation;
            
            debugLog(this.level, {
              action: 'ammo_donated_to_base',
              unitId: this.id,
              team: this.team.name,
              amount: actualDonation,
              baseAmmo: this.base.ammo,
              isHighLevel: this.level >= 5
            });
          }
        }
      }
      
      // Stop movement while at base (except for resupplying units which handle their own movement)
      this.vx = 0;
      this.vy = 0;
    }
    
    // Handle resupplying units separately - they get healing/ammo but keep moving with boids
    if (this.state === 'resupplying' && this.base && this.base.alive && this.base.isAtCenter && this.base.isAtCenter(this)) {
      const healPerMs = this.maxHealth / AI_CONFIG.HEAL_TIME;
      
      if (this.health < this.maxHealth) {
        this.health = Math.min(this.maxHealth, this.health + healPerMs * delta);
      }
      if (this.courage < this.maxCourage) {
        this.courage = Math.min(this.maxCourage, this.courage + delta / THRESHOLD_CONFIG.COURAGE_RECOVERY_DIVISOR);
      }
      if (this.ammo < this.maxAmmo && this.base.giveAmmo) {
        this.base.giveAmmo(this);
      }
      
      // Donate ammo to base if it needs it and soldier is fully supplied
      if (this.base.priority === 'get_ammo' && this.ammo >= this.maxAmmo * 0.8) {
        const readyToLeave = this.health >= this.maxHealth * 0.8 && this.courage >= this.maxCourage * 0.8 && this.ammo >= this.maxAmmo * 0.7;
        if (!readyToLeave) {
          // Level 5+ soldiers donate more ammo
          const donationMultiplier = this.level >= 5 ? COMBAT_CONFIG.HIGH_LEVEL_DONATION_MULTIPLIER : 1.0;
          const donationAmount = Math.min(this.ammo * COMBAT_CONFIG.AMMO_DONATION_PERCENTAGE * donationMultiplier, BASE_CONFIG.MISSILE_AMMO_COST * COMBAT_CONFIG.MISSILE_DONATION_AMOUNT * donationMultiplier);
          if (this.base.depositAmmo) {
            const actualDonation = this.base.depositAmmo(donationAmount);
            this.ammo -= actualDonation;
            
            debugLog(this.level, {
              action: 'ammo_donated_to_base_resupplying',
              unitId: this.id,
              team: this.team.name,
              amount: actualDonation,
              baseAmmo: this.base.ammo,
              isHighLevel: this.level >= 5
            });
          }
        }
      }
      
      // Let AI handle movement - don't override vx/vy here
    }
    
    // Update movement
    this.updateMovement(delta);
    
    // Check health
    if (this.health <= 0) {
      this.die();
    }
  }
  
  /**
   * Fire a projectile at the current target
   *
   * Called by SoldierAI when in firing range and fire rate cooldown is ready.
   * Uses predictive aim with skill-based accuracy.
   *
   * Accuracy improves with:
   * - Higher skill value (gained through leveling)
   * - Close range targets
   *
   * @param {Projectile[]} projectiles - Array to add new projectile to
   */
  fire(projectiles) {
    if (this.ammo <= 0) return;
    
    const target = this.ai.currentTarget;
    if (!target || !target.alive || target.health <= 0) return;
    
    // Get predicted position from AI
    const prediction = this.ai.predictTargetPosition(target);
    
    // Add some random spread
    const spread = Math.max(COMBAT_CONFIG.WEAPON_SPREAD_MIN, (COMBAT_CONFIG.MAX_SKILL_CAP - this.skill) / COMBAT_CONFIG.SKILL_TO_SPREAD_DIVISOR);
    const randomX = (Math.random() - 0.5) * spread * COMBAT_CONFIG.SPREAD_MULTIPLIER;
    const randomY = (Math.random() - 0.5) * spread * COMBAT_CONFIG.SPREAD_MULTIPLIER;
    
    // Calculate firing angle
    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const angle = Math.atan2(dy, dx);
    
    // Fire from edge of soldier
    const startX = this.x + Math.cos(angle) * (this.size + COMBAT_CONFIG.PROJECTILE_START_OFFSET);
    const startY = this.y + Math.sin(angle) * (this.size + COMBAT_CONFIG.PROJECTILE_START_OFFSET);
    
    // Create projectile
    const projectile = new Projectile(
      startX, startY,
      prediction.x + randomX,
      prediction.y + randomY,
      this.damage,
      this.color,
      this,
      this.canvas,
      target
    );
    
    projectiles.push(projectile);
    audioManager.playFire();
    
    // Note: ammo is decremented in SoldierAI.fireAt() to avoid dual deduction
    this.muzzleFlash = 1;
    
    debugLog(this.level, {
      action: 'firing',
      targetId: target.id,
      distance: getDistance(this, target).toFixed(1),
      ammo: this.ammo
    });
  }
  
  /**
   * Handle taking damage with soldier-specific effects
   *
   * In addition to base Unit damage handling:
   * - Reduces courage (affects willingness to fight)
   * - Triggers damage indicator visual effect
   * - May auto-target attacker if in range and not fleeing
   * - Notifies AI for retreat decision making
   *
   * @param {number} damage - Amount of damage to take
   * @param {Unit} attacker - Unit that dealt the damage (for revenge targeting)
   * @returns {boolean} True if soldier died from this damage
   */
  takeDamage(damage, attacker) {
    const died = super.takeDamage(damage, attacker);
    
    if (!died) {
      this.lastDamageTime = Date.now();
      this.courage = Math.max(0, this.courage - COMBAT_CONFIG.COURAGE_DAMAGE_REDUCTION);
      this.damageIndicator = 1; // Trigger hit effect
      
      // Track damage for AI retreat decisions
      if (this.ai && this.ai.onDamageTaken) {
        this.ai.onDamageTaken(damage);
      }
      
      // Auto-target attacker if valid and in range
      if (attacker && attacker.alive && attacker.health > 0 && 
          getDistance(this, attacker) < this.weaponRange * COMBAT_CONFIG.AUTO_TARGET_RANGE_MULTIPLIER &&
          this.state !== 'fleeing' && this.state !== 'seeking_base') {
        
        if (!this.carryingResource || this.carryingResource.type !== 'metal' || 
            this.health <= this.maxHealth * GAME_CONFIG.FLEE_HEALTH_THRESHOLD) {
          this.ai.currentTarget = attacker;
          this.ai.sendEvent('ENEMY_FOUND');
        }
      }
    }
    
    return died;
  }
  
  /**
   * Called when this soldier kills an enemy
   *
   * - Triggers level up (instant stat boost)
   * - Logs kill to Soul API if both units have souls
   * - Marks corpse position for potential looting
   *
   * @param {Unit} victim - The killed enemy unit
   */
  onKill(victim) {
    if (this.ai && this.ai.onDamageDealt) {
      this.ai.onDamageDealt(victim.maxHealth);
    }
    
    // Log soul combat kill if both units have souls
    if (this.incarnationId && victim.incarnationId) {
      // Import at runtime to avoid circular dependency
      import('../systems/SoulforgeAPI.js').then(({ soulforgeAPI }) => {
        const context = {
          range: Math.sqrt(Math.pow(this.x - victim.x, 2) + Math.pow(this.y - victim.y, 2)),
          weapon: 'rifle',
          level_difference: this.level - victim.level,
          attacker_health: this.health / this.maxHealth
        };
        soulforgeAPI.logCombatKill(this.id, victim.id, context);
      });
    }
    
    this.levelUp();
    
    // Mark that we just killed someone and should check for their corpse
    if (this.ai) {
      this.ai.justKilledEnemy = true;
      this.ai.killedEnemyPosition = { x: victim.x, y: victim.y };
    }
  }
  
  /**
   * Level up the soldier (instant stat boost)
   *
   * Triggered by:
   * - Killing an enemy (combat XP)
   * - Looting a corpse (instant level)
   *
   * Stat increases:
   * - Size, health, weapon range, damage: multiply by LEVEL_UP_MULTIPLIER (1.1)
   * - Speed: multiply by LEVEL_SPEED_MULTIPLIER (1.05)
   * - Skill: add SKILL_INCREASE_PER_LEVEL (improves accuracy)
   * - Fire rate: multiply by FIRE_RATE_IMPROVEMENT (faster shooting)
   * - Perception range: add bonus (see enemies from further away)
   * - Max ammo: multiply by LEVEL_UP_MULTIPLIER
   *
   * Special: Reaching MAX_LEVEL (15) activates berserker mode
   */
  levelUp() {
    if (this.level >= GAME_CONFIG.MAX_LEVEL) {
      // Already max level - just heal and refill ammo
      this.health = this.maxHealth;
      this.ammo = this.maxAmmo;
      this.kills++;
      return;
    }

    this.level++;
    audioManager.playLevelUp();

    // Stat increases (all multiplicative for exponential growth)
    this.size *= GAME_CONFIG.LEVEL_UP_MULTIPLIER;
    this.maxHealth *= GAME_CONFIG.LEVEL_UP_MULTIPLIER;
    this.health = this.maxHealth;
    this.weaponRange *= GAME_CONFIG.LEVEL_UP_MULTIPLIER;
    this.damage *= GAME_CONFIG.LEVEL_UP_MULTIPLIER;
    this.speed *= MOVEMENT_CONFIG.LEVEL_SPEED_MULTIPLIER;
    this.skill = Math.min(COMBAT_CONFIG.MAX_SKILL_CAP, this.skill + COMBAT_CONFIG.SKILL_INCREASE_PER_LEVEL);
    this.ai.fireRate = Math.max(AI_CONFIG.MIN_FIRE_RATE, this.ai.fireRate * COMBAT_CONFIG.FIRE_RATE_IMPROVEMENT);
    this.perceptionRange += AI_CONFIG.PERCEPTION_RANGE * AI_CONFIG.PERCEPTION_LEVEL_BONUS;
    this.maxAmmo = Math.ceil(this.maxAmmo * GAME_CONFIG.LEVEL_UP_MULTIPLIER);
    this.ammo = this.maxAmmo;
    this.kills++;
    
    // Update AI perception range
    if (this.ai) {
      this.ai.perceptionRange = this.perceptionRange;
      this.ai.weaponRange = this.weaponRange;
    }
    
    // Activate berserker mode at max level
    if (this.level >= GAME_CONFIG.MAX_LEVEL && !this.isBerserker) {
      this.activateBerserkerMode();
    }
  }
  
  /**
   * Activate berserker mode (max level power-up)
   *
   * When soldier reaches MAX_LEVEL (15), they become a berserker with:
   * - Massive damage multiplier (3x by default)
   * - Speed boost (1.5x)
   * - Faster fire rate (0.5x = twice as fast)
   * - Size increase (1.2x)
   * - Glowing visual effect
   * - Can revive destroyed bases
   * - Highly aggressive AI behavior
   */
  activateBerserkerMode() {
    this.isBerserker = true;
    this.berserkerGlow = 1;

    // Apply berserker stat multipliers
    this.damage *= BERSERKER_CONFIG.DAMAGE_MULTIPLIER;
    this.speed *= BERSERKER_CONFIG.SPEED_MULTIPLIER;
    this.ai.fireRate *= BERSERKER_CONFIG.FIRE_RATE_MULTIPLIER;
    this.size *= BERSERKER_CONFIG.SIZE_MULTIPLIER;
    
    debugLog(this.level, {
      action: 'berserker_activated',
      damage: this.damage.toFixed(1),
      speed: this.speed.toFixed(1)
    });
    
    audioManager.playLevelUp();
  }
  
  /**
   * Apply separation force to prevent teammate clustering (boid behavior)
   *
   * Soldiers naturally repel nearby teammates to spread out across the battlefield.
   * This prevents clustering which:
   * - Makes teams easier to target
   * - Causes pathfinding congestion
   * - Looks unnatural
   *
   * Uses inverse distance weighting - closer teammates = stronger repulsion
   *
   * @param {Soldier[]} teammates - Nearby allied soldiers to separate from
   */
  applySeparationForce(teammates) {
    let separationX = 0;
    let separationY = 0;
    let nearbyCount = 0;
    
    for (let teammate of teammates) {
      const distance = getDistance(this, teammate);
      
      if (distance < AI_CONFIG.SEPARATION_RADIUS && distance > 0) {
        const repulsionStrength = (AI_CONFIG.SEPARATION_RADIUS - distance) / AI_CONFIG.SEPARATION_RADIUS;
        const dx = this.x - teammate.x;
        const dy = this.y - teammate.y;
        
        const magnitude = Math.sqrt(dx * dx + dy * dy);
        if (magnitude > 0) {
          separationX += (dx / magnitude) * repulsionStrength;
          separationY += (dy / magnitude) * repulsionStrength;
          nearbyCount++;
        }
      }
    }
    
    if (nearbyCount > 0) {
      separationX /= nearbyCount;
      separationY /= nearbyCount;
      
      this.vx += separationX * AI_CONFIG.SEPARATION_FORCE;
      this.vy += separationY * AI_CONFIG.SEPARATION_FORCE;
    }
  }
  

  /**
   * Apply avoidance force to steer clear of enemy bases
   *
   * Soldiers avoid walking into enemy base firing range unless:
   * - They are berserkers actively targeting the base
   * - They have no other choice (cornered)
   *
   * Uses same inverse distance weighting as separation force.
   *
   * @param {TeamBase[]} bases - All bases in the game
   */
  applyBaseAvoidanceForce(bases) {
    if (!bases || bases.length === 0) return;
    
    let avoidanceX = 0;
    let avoidanceY = 0;
    let nearbyBaseCount = 0;
    
    for (let base of bases) {
      if (!base.alive || base.team.id === this.team.id) continue;
      if (this.isBerserker && this.ai.currentTarget === base) continue;
      
      const distance = getDistance(this, base);
      
      if (distance < AI_CONFIG.BASE_AVOIDANCE_RADIUS && distance > 0) {
        const repulsionStrength = (AI_CONFIG.BASE_AVOIDANCE_RADIUS - distance) / AI_CONFIG.BASE_AVOIDANCE_RADIUS;
        const dx = this.x - base.x;
        const dy = this.y - base.y;
        
        const magnitude = Math.sqrt(dx * dx + dy * dy);
        if (magnitude > 0) {
          avoidanceX += (dx / magnitude) * repulsionStrength;
          avoidanceY += (dy / magnitude) * repulsionStrength;
          nearbyBaseCount++;
        }
      }
    }
    
    if (nearbyBaseCount > 0) {
      avoidanceX /= nearbyBaseCount;
      avoidanceY /= nearbyBaseCount;
      
      this.vx += avoidanceX * AI_CONFIG.BASE_AVOIDANCE_FORCE;
      this.vy += avoidanceY * AI_CONFIG.BASE_AVOIDANCE_FORCE;
    }
  }
  
  /**
   * Apply knockback force from base missile explosions
   *
   * When a base missile explodes near the soldier, they get knocked back.
   * This creates dynamic combat where soldiers can't just rush bases.
   *
   * @param {number} force - Knockback force magnitude
   * @param {number} fromX - X coordinate of explosion center
   * @param {number} fromY - Y coordinate of explosion center
   */
  applyKnockback(force, fromX, fromY) {
    const dx = this.x - fromX;
    const dy = this.y - fromY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance > 0) {
      this.vx += (dx / distance) * force;
      this.vy += (dy / distance) * force;
      this.knockbackTime = MOVEMENT_CONFIG.KNOCKBACK_DURATION; // 0.5 seconds at 60fps
    }
  }
  
  // Resource management
  releaseNebulaSlot() {
    if (this.carryingResource && this.carryingResource.type === 'nebula' && 
        this.carryingResource.gatherers && this.carryingResource.gatherers.has(this)) {
      this.carryingResource.stopCollection(this);
    }
  }
  
  dropResource(resources) {
    if (this.carryingResource) {
      if (this.carryingResource.type === 'nebula') {
        this.releaseNebulaSlot();
      } else if (typeof this.carryingResource.x === 'undefined') {
        resources.push(new Resource(this.x, this.y, this.carryingResource.type, this.carryingResource.amount));
      }
      this.carryingResource = null;
    }
    
    // Drop metal cargo - for now just clear it (would need Metal import to drop as resources)
    if (this.metalCargo.length > 0) {
      this.metalCargo = [];
    }
  }
  
  // Get total cargo count
  getCargoCount() {
    const count = this.metalCargo.reduce((sum, metal) => sum + metal.amount, 0);
    if (count > 0) {
      // console.log(`[CARGO] ${this.team.name} soldier ${this.id} getCargoCount: ${count}, array:`, JSON.stringify(this.metalCargo));
    }
    return count;
  }
  
  // Check if cargo is full
  isCargoFull() {
    return this.getCargoCount() >= BASE_CONFIG.SOLDIER_CARGO_CAPACITY;
  }
  
  // Add metal to cargo
  addMetalToCargo(metalResource) {
    // console.log(`[CARGO] ${this.team.name} soldier ${this.id} BEFORE add: metalCargo =`, JSON.stringify(this.metalCargo));

    this.metalCargo.push({
      type: 'metal',
      amount: metalResource.amount || 1
    });

    // console.log(`[CARGO] ${this.team.name} soldier ${this.id} AFTER add: metalCargo =`, JSON.stringify(this.metalCargo), `Total count: ${this.getCargoCount()}`);
  }
  
  // State change handler
  onStateChange(oldState, newState) {
    // Release resources when switching to combat states
    if ((newState === 'fighting' || newState === 'fleeing' || newState === 'seeking_base') && 
        oldState !== 'returning_resource') {
      this.releaseNebulaSlot();
    }
  }
  
  // Custom rendering for soldiers
  render(ctx) {
    if (!this.alive) return;
    
    ctx.save();
    
    // Apply fade-in opacity
    ctx.globalAlpha = this.opacity;
    
    // Berserker glow
    if (this.isBerserker) {
      ctx.shadowBlur = BERSERKER_CONFIG.GLOW_RADIUS * this.berserkerGlow;
      ctx.shadowColor = BERSERKER_CONFIG.GLOW_COLOR;
      
      for (let i = 0; i < BERSERKER_CONFIG.GLOW_RENDER_PASSES; i++) {
        ctx.fillStyle = this.color;
        ctx.fillRect(this.x - this.size/2, this.y - this.size/2, this.size, this.size);
      }
    }
    
    // Muzzle flash
    if (this.muzzleFlash > 0) {
      ctx.shadowBlur = ANIMATION_CONFIG.MUZZLE_FLASH_BLUR * this.muzzleFlash;
      ctx.shadowColor = this.color;
    }
    
    // Use base rendering with opacity still applied
    super.render(ctx);
    
    // Damage indicator - red expanding circle
    if (this.damageIndicator > 0) {
      const progress = 1 - this.damageIndicator;
      const alpha = Math.sin(progress * Math.PI) * this.opacity; // Apply fade-in to damage indicator too
      const radius = (this.size + progress * COMBAT_CONFIG.DAMAGE_INDICATOR_RADIUS) * COMBAT_CONFIG.DAMAGE_INDICATOR_SCALE;
      ctx.strokeStyle = `rgba(255,0,0,${alpha})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(this.x, this.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    ctx.restore();
    
    // Additional soldier UI (only render debug info in debug mode)
    if (isDebugMode()) {
      this.renderDebugInfo(ctx);
    }
  }
  
  
  renderDebugInfo(ctx) {
    ctx.save();
    ctx.globalAlpha = this.opacity;
    
    // Show state text
    if (DEBUG_VISUALIZATION_CONFIG.showUnitState) {
      ctx.fillStyle = DEBUG_VISUALIZATION_CONFIG.colors.unitStateText;
      ctx.font = UI_CONFIG.FONT_MEDIUM;
      ctx.textAlign = 'left';
      ctx.fillText(this.state, this.x + this.size + UI_CONFIG.STATE_TEXT_OFFSET_X, this.y);
      
      // Show additional info if carrying resource
      if (this.carryingResource) {
        ctx.fillStyle = '#ff0';
        ctx.fillText(`[${this.carryingResource.type}]`, this.x + this.size + UI_CONFIG.STATE_TEXT_OFFSET_X, this.y + UI_CONFIG.RESOURCE_TYPE_OFFSET_Y);
      }
    }
    
    // Show perception range
    if (DEBUG_VISUALIZATION_CONFIG.showPerceptionRadius) {
      ctx.strokeStyle = this.color;
      ctx.globalAlpha = UI_CONFIG.PERCEPTION_CIRCLE_ALPHA * this.opacity;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.perceptionRange, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = this.opacity;
    }
    
    // Show weapon range
    if (DEBUG_VISUALIZATION_CONFIG.showWeaponRadius) {
      ctx.strokeStyle = '#f00';
      ctx.globalAlpha = UI_CONFIG.PERCEPTION_CIRCLE_ALPHA * this.opacity;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.weaponRange, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = this.opacity;
    }
    
    // Level indicator
    if (this.level > 1) {
      ctx.fillStyle = this.isBerserker ? BERSERKER_CONFIG.GLOW_COLOR : (this.level >= GAME_CONFIG.MAX_LEVEL ? '#f0f' : '#fff');
      ctx.font = this.isBerserker ? 'bold ' + UI_CONFIG.FONT_MEDIUM : UI_CONFIG.FONT_SMALL;
      ctx.textAlign = 'center';
      const levelText = this.isBerserker ? `BERSERKER` : `L${this.level}`;
      ctx.fillText(levelText, this.x, this.y - this.size - UI_CONFIG.LEVEL_TEXT_OFFSET_Y);
      
      // Skill dots
      const skillDots = Math.floor(this.skill / COMBAT_CONFIG.SKILL_INCREASE_PER_LEVEL);
      if (skillDots > 0) {
        ctx.fillStyle = '#ff0';
        for (let i = 0; i < skillDots; i++) {
          ctx.fillRect(
            this.x - (skillDots * RENDER_CONFIG.SKILL_DOT_SPACING / 2) + (i * RENDER_CONFIG.SKILL_DOT_SPACING), 
            this.y + this.size + 3, 
            RENDER_CONFIG.SKILL_DOT_SIZE, 
            RENDER_CONFIG.SKILL_DOT_SIZE
          );
        }
      }
    }
    
    // Show courage bar if not full
    if (this.courage < this.maxCourage) {
      const barWidth = this.size * 2;
      const courageY = this.y - this.size - UI_CONFIG.COURAGE_BAR_OFFSET_Y;
      ctx.fillStyle = '#333';
      ctx.fillRect(this.x - barWidth/2, courageY, barWidth, RENDER_CONFIG.COURAGE_BAR_HEIGHT);
      
      const couragePercent = this.courage / this.maxCourage;
      ctx.fillStyle = '#88f';
      ctx.fillRect(this.x - barWidth/2, courageY, barWidth * couragePercent, RENDER_CONFIG.COURAGE_BAR_HEIGHT);
    }
    
    // Show cargo indicator if carrying metal
    const cargoCount = this.getCargoCount();
    // console.log(`[CARGO RENDER] ${this.team.name} soldier ${this.id}: cargoCount = ${cargoCount}, metalCargo =`, this.metalCargo);
    if (cargoCount > 0) {
      ctx.fillStyle = '#aaa';
      ctx.font = UI_CONFIG.FONT_SMALL;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`Metal: ${cargoCount}/${BASE_CONFIG.SOLDIER_CARGO_CAPACITY}`, 
                   this.x, 
                   this.y + this.size + 15);
      // console.log(`[CARGO RENDER] Drawing text at ${this.x}, ${this.y + this.size + 15}`);
    }
    
    // Let AI render debug info
    if (this.ai) {
      this.ai.renderDebug(ctx);
    }
    
    ctx.restore();
  }
  
  // Clear target tracking
  clearTarget() {
    this.target = null;
    if (this.ai) {
      this.ai.currentTarget = null;
      this.ai.targetPrevX = null;
      this.ai.targetPrevY = null;
      this.ai.targetInitialized = false;
      this.ai.lastPredictedX = 0;
      this.ai.lastPredictedY = 0;
      this.ai.offScreenPredictionCount = 0;
    }
    this.lastPredictedX = 0;
    this.lastPredictedY = 0;
  }
  


  // Override onDeath to release nebula slot
  onDeath() {
    // Log soul death event if this soldier has a soul
    if (this.incarnationId) {
      // Import at runtime to avoid circular dependency
      import('../systems/SoulforgeAPI.js').then(({ soulforgeAPI }) => {
        soulforgeAPI.endIncarnation(this.id, {
          outcome: 'death',
          final_level: this.level,
          kills: this.kills,
          lifetime: Date.now() - this.spawnTime,
          last_position: { x: this.x, y: this.y },
          last_state: this.state
        });
      });
    }
    
    // Release nebula slot if we were gathering
    if (this.ai && this.ai.releaseNebulaSlot) {
      this.ai.releaseNebulaSlot();
    }

    // Drop any carried resources
    if (this.carryingResource) {
      this.carryingResource = null;
    }
    
    // Remove from base repairers if we were repairing
    if (this.isRepairingBase && this.base) {
      this.base.removeRepairer(this);
      this.isRepairingBase = false;
    }

    // Clear as target for other units
    if (this.projectiles) {
      for (let unit of this.projectiles) {
        if (unit.ai && unit.ai.currentTarget === this) {
          unit.ai.currentTarget = null;
        }
      }
    }
  }

}

// Reset ID counter for compatibility
Soldier.nextId = 1;